/* eslint-disable no-param-reassign */
/* eslint-disable no-case-declarations */
import { isEmpty } from 'lodash';
import { MomentInput } from 'moment/moment';
import * as constants from 'common/constants/financials';
import { GridDateEditor, GridDateValueViewer } from 'components/FeaturedSpreadsheet/components';
import { Action } from 'models/interfaces/spreadsheet-types/basic-types';
import { FinancialsDate } from 'pages/Financials/components';
import reverseParser from 'pages/Financials/income-statement/utilities/reverseParser';
import { columnUpdateActionCallback } from 'pages/Financials/utilities';
import { gridShortDate, isValidDate, toString } from 'utilities';
import { IncomeStatementCell, IncomeStatementConditionsProps } from './types';
import DocumentReferenceValueViewer from '../../../../components/ScalarSpreadsheet/utilities/document-reference-value-viewer/DocumentReferenceValueViewer';
import ReferencesValueViewer from '../../../../components/ScalarSpreadsheet/utilities/references-value-viewer/ReferencesValueViewer';

const handleFinancialsValueCell = (cell: IncomeStatementCell, isLtmOrNtm: boolean) => {
  if (isEmpty(toString(cell.value)) && (isLtmOrNtm || isEmpty(cell.expr))) {
    cell.expr = cell.defaultExpr;
  }
};

const conditions = ({ cell, tableData, cells: scope, onCellsChanged }: IncomeStatementConditionsProps) => {
  const { alias, isLTM, isNTM } = cell;
  const { isDisabled } = tableData;
  const isLtmOrNtm = isLTM || isNTM;
  const isFiscalYear = !isLtmOrNtm;

  const addExtraAction = ({
    targetCell,
    title,
    callback,
    buttonText,
    type,
  }: {
    targetCell: IncomeStatementCell;
    title: string;
    callback: () => void;
    buttonText: string;
    type: string | null;
  }) => {
    if (targetCell.action?.type !== 'update_col') {
      targetCell.extraActions = [
        {
          title,
          buttonText,
          callback,
          type,
        },
      ] as Action[];
    }
  };

  const enableUpdateColumn = () => {
    if (cell.needs_actualization) {
      addExtraAction({
        targetCell: cell,
        title: constants.COLUMN_UPDATED_ACTION_TITLE,
        callback: () =>
          columnUpdateActionCallback({
            cell,
            scope,
            onCellsChanged,
            reverseParser,
          }),
        type: 'update_col',
        buttonText: constants.COLUMN_UPDATED,
      });
    }
  };

  const enableEditableDate = () => {
    if (isValidDate(cell.value as MomentInput)) {
      cell.readOnly = isDisabled;
      cell.className = 'date-editor';
      cell.value = gridShortDate(cell.value as MomentInput);
      cell.gridDateComponent = FinancialsDate as any;
      cell.dataEditor = (props: any) => <GridDateEditor {...props} />;
      cell.valueViewer = (props: any) => <GridDateValueViewer {...props} />;

    }
  };

  switch (alias) {
    case constants.FIRST_ROW_ALIAS:
      if (cell.rowSpan === 3) {
        enableUpdateColumn();
      }
      break;
    case constants.SECOND_ROW_ALIAS:
      if (isLtmOrNtm) {
        enableEditableDate();
      } else if (cell.isProjectionPeriod && isFiscalYear) {
        enableUpdateColumn();
      }
      break;
    case constants.TOTAL_REVENUE_ALIAS:
    case constants.TOTAL_COST_OF_SALES_ALIAS:
    case constants.OPERATING_EXPENSES_ALIAS:
    case constants.ADJUSTED_EBITDA_ALIAS:
    case constants.DEPRECIATION_EXPENSE_ALIAS:
    case constants.AMORTIZATION_EXPENSE_ALIAS:
    case constants.INTEREST_EXPENSE_ALIAS:
    case constants.OTHER_EXPENSE_ALIAS:
    case constants.INCOME_TAXES_ALIAS:
      handleFinancialsValueCell(cell, isLtmOrNtm);
      break;
    default:
      break;
  }

  // Add value viewer for cells with mapping references
  if (cell.mappingReferences && cell.mappingReferences.length > 0) {
    cell.valueViewer = (props: any) => <ReferencesValueViewer {...props} />;
    cell.className = 'cell-with-references';
  }

  if (cell.columnarReferences && cell.columnarReferences?.length > 0) {
    cell.valueViewer = (props: any) => <DocumentReferenceValueViewer {...props} />;
  }

  return cell;
};

export default conditions;
